import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { Input } from '@/components/ui/input';
import PhoneInput from '@/components/PhoneInput';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Eye, EyeOff, ArrowRight } from 'lucide-react';

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await login(phone, password, rememberMe);
      const u = res?.data?.user;
      if (u?.must_reset_password) { navigate('/force-reset'); return; }
      const returnUrl = sessionStorage.getItem('besq_return');
      if (returnUrl) {
        sessionStorage.removeItem('besq_return');
        navigate(returnUrl);
      } else {
        navigate(u?.role && u.role !== 'CUSTOMER' ? '/admin' : '/');
      }
    } catch (err) {
      setError(err?.response?.data?.error || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-72px)] flex items-center justify-center px-6 py-16 bg-background" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-12">
          <div className="eyebrow mb-4">Welcome</div>
          <h1 className="font-display font-light text-ink leading-none tracking-[-0.015em]" style={{ fontSize: 48 }}>Welcome back</h1>
          <p className="mt-4 text-ink/70" style={{ fontSize: 15 }}>Sign in with your mobile number and password</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
          <div>
            <Label htmlFor="phone" className="uppercase tracking-luxe text-[11px] font-semibold text-ink-muted mb-2 block">Mobile Number</Label>
            <PhoneInput id="phone" value={phone} onChange={setPhone} required data-testid="phone-input" className="!rounded-none border-t-0 border-l-0 border-r-0 border-b border-ink/20 focus-visible:ring-0 focus-visible:border-gold" />
          </div>
          <div>
            <Label htmlFor="password" className="uppercase tracking-luxe text-[11px] font-semibold text-ink-muted mb-2 block">Password</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="Enter your password"
                data-testid="password-input"
                className="rounded-none border-t-0 border-l-0 border-r-0 border-b border-ink/20 focus-visible:ring-0 focus-visible:border-gold px-0 pr-10 text-[16px]"
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                className="absolute right-0 top-1/2 -translate-y-1/2 text-ink-muted hover:text-ink"
                data-testid="toggle-password-visibility"
                aria-label="Toggle password visibility"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between pt-2">
            <label className="flex items-center gap-2.5 cursor-pointer">
              <Checkbox checked={rememberMe} onCheckedChange={setRememberMe} data-testid="remember-me" className="rounded-none border-ink/40" />
              <span className="text-[14px] text-ink">Remember me · 30 days</span>
            </label>
            <Link to="/forgot-password" className="text-[13px] text-ink hover:text-gold underline underline-offset-4" data-testid="forgot-password-link">
              Forgot password?
            </Link>
          </div>

          {error && <p className="text-[13px] text-destructive uppercase tracking-luxe font-medium" data-testid="login-error">{error}</p>}

          <button
            type="submit"
            disabled={loading}
            data-testid="login-btn"
            className="w-full inline-flex items-center justify-center gap-3 border border-ink bg-ink text-background hover:bg-transparent hover:text-ink uppercase tracking-luxe font-medium py-4 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ fontSize: 13 }}
          >
            {loading ? 'Signing in…' : (<>Sign In <ArrowRight className="w-4 h-4" /></>)}
          </button>

          <p className="text-center text-ink-muted pt-4" style={{ fontSize: 14 }} data-testid="register-link">
            New to Besq?{' '}
            <Link to="/register" className="text-ink font-medium underline underline-offset-4 hover:text-gold">Create an account</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
